import React, { useRef, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Award, ShieldCheck, Microscope, Heart, Star, Activity, Shield, Send, ArrowUpRight } from 'lucide-react';
import SEO from '../components/SEO';

gsap.registerPlugin(ScrollTrigger);

const AboutPage = () => {
  const containerRef = useRef(null);


  const milestones = [
    { year: 'Mission', event: 'Quality & Compassion', desc: 'To deliver reliable and accessible veterinary care with a focus on quality and compassion.', color: 'text-primary' },
    { year: 'Vision', event: 'Trusted Provider', desc: 'To be recognized as a trusted pet healthcare provider in Hyderabad.', color: 'text-blue-500' },
  ];

  const pillars = [
    { icon: <ShieldCheck size={28} />, title: 'Accurate Diagnosis', desc: 'State-of-the-art diagnostic protocols for every clinical case.' },
    { icon: <Microscope size={28} />, title: 'Advanced Treatment', desc: 'Modern medical and surgical methods for optimal recovery.' },
    { icon: <Heart size={28} />, title: 'Compassionate Care', desc: 'Treating every pet with love, care, and medical excellence.' },
    { icon: <Star size={28} />, title: 'Vanasthalipuram Hub', desc: 'The most reliable choice for pet healthcare in the region.' }
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.about-hero-line', { y: 60, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.12, duration: 1, ease: 'power3.out', delay: 0.2 });
      gsap.fromTo('.timeline-item', { x: -50, opacity: 0 }, {
        x: 0, opacity: 1, stagger: 0.2, duration: 1.5, ease: 'expo.out',
        scrollTrigger: { trigger: '.timeline-container', start: 'top 80%' }
      });
      gsap.fromTo('.pillar-card', { y: 40, opacity: 0, scale: 0.95 }, {
        y: 0, opacity: 1, scale: 1, stagger: 0.12, duration: 0.8, ease: 'power3.out',
        scrollTrigger: { trigger: '.pillars-grid', start: 'top 80%' }
      });
      gsap.to('.about-glow', { y: '-15%', ease: 'none', scrollTrigger: { trigger: '.about-hero', scrub: true } });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-white" ref={containerRef}>
      <SEO 
        title="About Dr. Suresh Pet Care | Best Veterinarian in Hyderabad"
        description="Learn about Dr. Suresh Pet Care Hospital & Surgical Center in Vanasthalipuram. Leading veterinary sanctuary directed by Dr. T Suresh Babu since 2003."
        canonical="/about"
      />


      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="about-hero pt-[140px] md:pt-[160px] pb-24 relative overflow-hidden min-h-[90vh] flex flex-col justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-[#fafafa] via-white to-primary/5 z-0" />
        <div className="about-glow absolute top-[15%] right-[5%] w-[500px] h-[500px] bg-primary/8 rounded-full blur-[150px] pointer-events-none" />
        <div className="absolute bottom-0 left-[10%] w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-10">
              <div className="about-hero-line inline-flex items-center gap-3 px-5 py-2.5 bg-primary/5 rounded-full border border-primary/15">
                <Award size={14} className="text-primary" />
                <span className="text-[10px] font-black tracking-[0.3em] uppercase text-primary">Mastering Surgical Care Since 2003</span>
              </div>
              <h1 className="about-hero-line text-navy font-display font-bold leading-[0.9] tracking-tight text-[clamp(2.5rem,7vw,5.5rem)] md:text-5xl lg:text-7xl">
                Leading destination for <br /><em className="font-normal text-primary not-italic text-3xl md:text-5xl lg:text-7xl">Pet Healthcare.</em>
              </h1>
              <p className="about-hero-line text-navy/60 text-xl leading-relaxed max-w-xl font-medium">
                Dr. Suresh Pet Care Hospital & Surgical Center is a well-equipped veterinary clinic in Vanasthalipuram, committed to delivering quality medical and surgical care for pets.
              </p>
              <div className="about-hero-line flex flex-col sm:flex-row gap-4">
                <a href="https://wa.me/919848954921" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-primary text-white px-8 py-4 rounded-full font-bold text-sm hover:shadow-[0_20px_40px_rgba(3,137,3,0.3)] transition-all duration-500 group">
                  <Send size={16} className="group-hover:translate-x-0.5 transition-transform" /> Book a Consultation
                </a>
                <a href="#founder" className="inline-flex items-center gap-3 text-navy px-8 py-4 rounded-full font-bold text-sm border border-navy/10 hover:bg-navy/5 transition-all">
                  <ArrowUpRight size={16} className="text-primary" /> Meet Dr. Suresh Babu
                </a>
              </div>
            </div>

            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.5, ease: [0.16,1,0.3,1] }}
              className="relative rounded-[4rem] overflow-hidden shadow-2xl aspect-[4/3] group border border-navy/5">
              <img src="/images/clinic/clinic_exterior.png" alt="Dr. Suresh Pet Care Clinic — Vanasthalipuram Main Campus"
                className="w-full h-full object-cover transition-transform duration-[10s] group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent" />
              <div className="absolute bottom-8 left-8 text-white z-10">
                <p className="text-[10px] font-bold tracking-[0.4em] uppercase opacity-60 mb-1">The Main Sanctuary</p>
                <h3 className="text-2xl font-bold">Vanasthalipuram Branch</h3>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FOUNDER SECTION ─────────────────────────────── */}
      <section id="founder" className="py-32 bg-[#010816] text-white relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-[50vw] h-[50vw] bg-primary/5 rounded-full blur-[180px] pointer-events-none" />
        <div className="absolute top-0 left-0 w-[40vw] h-[40vw] bg-blue-900/10 rounded-full blur-[180px] pointer-events-none" />
        <div className="section-container relative z-10">
          <div className="text-center space-y-4 mb-24">
            <span className="text-primary font-black tracking-[0.5em] uppercase text-[10px]">Medical Leadership</span>
            <h2 className="text-5xl md:text-7xl font-bold text-white">Meet Our <em className="font-normal text-primary not-italic">Founder.</em></h2>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-0 rounded-[4rem] overflow-hidden bg-white/5 backdrop-blur-3xl border border-white/10 shadow-2xl">
              <div className="relative h-[500px] lg:h-auto overflow-hidden group">
                <img src="/images/doctor.png" alt="Dr. T Suresh Babu — Lead Surgeon, Dr. Suresh Pet Care Hyderabad"
                  className="w-full h-full object-cover lg:object-[95%_top] object-[95%_20%] transition-transform duration-[5s] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#010816]/60 to-transparent lg:hidden" />
                <div className="absolute bottom-4 right-4 p-3 bg-primary/90 backdrop-blur-xl rounded-3xl shadow-xl flex flex-col items-center">
                  <Star fill="white" size={16} className="mb-1" />
                  <span className="font-black text-xs uppercase tracking-tighter text-white">23+ Yrs</span>
                </div>
              </div>

              <div className="p-12 md:p-20 flex flex-col justify-center space-y-10">
                <div className="space-y-4">
                  <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-white">Dr. T Suresh Babu</h3>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-primary/20 rounded-lg text-primary font-bold text-[10px] uppercase tracking-widest border border-primary/20">Lead Surgeon</span>
                    <span className="text-white/60 font-bold text-sm tracking-widest uppercase">MVSc, Surgery</span>
                  </div>
                </div>
                <p className="text-white/80 text-xl leading-relaxed italic font-light">
                  "Every case is a testament to the trust a family places in us. We don't just treat animals; we restore the joy of companionship through technical precision and deep empathy."
                </p>
                <div className="grid grid-cols-2 gap-8 pt-10 border-t border-white/10">
                  {[
                    { label: 'Successful Cases', value: '20,000+', icon: <Activity size={18} /> },
                    { label: 'Surgical Precision', value: '99%', icon: <Shield size={18} /> }
                  ].map((stat, i) => (
                    <div key={i} className="space-y-2">
                      <div className="flex items-center gap-3 text-primary">
                        {stat.icon}
                        <span className="text-2xl font-black">{stat.value}</span>
                      </div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PILLARS ─────────────────────────────────────── */}
      <section className="py-32 bg-white">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-24 items-center">
            <div className="space-y-10">
              <div>
                <p className="text-primary font-black uppercase tracking-[0.3em] text-[10px] mb-4">Medical Philosophy</p>
                <h2 className="text-navy font-bold leading-[0.92] tracking-tight text-5xl md:text-6xl">
                  Crafting the <br /><em className="font-normal text-primary not-italic">Sanctuary</em> Standard.
                </h2>
              </div>
              <div className="space-y-6 text-navy/60 text-lg leading-relaxed">
                <p>We understand that pets are family, and we treat every pet with love, care, and medical excellence. Our sanctuary focuses on providing reliable treatments that pet parents in Hyderabad have trusted for years.</p>
                <p>From accurate diagnosis to advanced treatment methods, we ensure your pet lives a healthy and happy life through clinical authority and compassion.</p>
              </div>
            </div>
            <div className="pillars-grid grid grid-cols-1 sm:grid-cols-2 gap-5">
              {pillars.map((item, i) => (
                <div key={i} className="pillar-card bg-[#f8faff] border border-navy/5 p-8 rounded-[2.5rem] space-y-5 hover:bg-white hover:shadow-2xl hover:-translate-y-2 transition-all duration-700 group">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all duration-500">
                    {item.icon}
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-bold text-navy text-base group-hover:text-primary transition-colors">{item.title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TIMELINE ─────────────────────────────────────── */}
      <section className="py-32 bg-[#f8faff] border-t border-navy/5 relative overflow-hidden">
        <div className="section-container timeline-container">
          <div className="max-w-3xl mb-24">
            <p className="text-primary font-black tracking-[0.5em] uppercase text-[10px] mb-6">The Evolution</p>
            <h2 className="text-5xl md:text-7xl font-bold tracking-tight text-navy">
              A Journey of <br /><em className="font-normal text-primary not-italic">Technical Impact.</em>
            </h2>
          </div>

          <div className="space-y-24">
            {milestones.map((item, i) => (
              <div key={i} className="timeline-item flex flex-col md:flex-row gap-10 md:gap-20 items-start group relative">
                <div className="md:w-1/3">
                  <p className={`text-[7rem] sm:text-[9rem] font-bold ${item.color} opacity-10 leading-none group-hover:opacity-20 transition-opacity duration-700`}>{item.year}</p>
                </div>
                <div className="md:w-2/3 md:pt-8 space-y-5">
                  <div className="flex items-center gap-4">
                    <div className={`h-[2px] w-12 ${item.color.replace('text-', 'bg-')}`} />
                    <h3 className="text-3xl md:text-4xl font-bold text-navy">{item.event}</h3>
                  </div>
                  <p className="text-navy/50 text-xl max-w-2xl leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default AboutPage;
